import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { locationsData, consultScenarios, prepItems, heroBg } from "@/mocks/locations";
import DistanceCalculatorSection from "./components/DistanceCalculatorSection";
import SEOMeta from "@/components/feature/SEOMeta";

const siteUrl = (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) || "https://chronosspace.com";

export default function Locations() {
  const { t } = useTranslation();
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    setMapLoaded(false);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "全省服務據點｜速築美宅家 Chronos Space",
    "description": "速築美宅家 Chronos Space 提供台北、新竹、台中、台南、屏東與全省自地自建、危老重建、建築設計與預製工法諮詢服務。",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 台北", "address": { "@type": "PostalAddress", "streetAddress": "台北市萬華區漢中街195號", "addressLocality": "台北市", "addressCountry": "TW" } } },
        { "@type": "ListItem", "position": 2, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 新竹", "address": { "@type": "PostalAddress", "streetAddress": "新竹縣竹北市自強南路372號3樓", "addressLocality": "新竹縣", "addressCountry": "TW" } } },
        { "@type": "ListItem", "position": 3, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 台中設計總部", "address": { "@type": "PostalAddress", "streetAddress": "台中市北區中清路一段79號6樓", "addressLocality": "台中市", "addressCountry": "TW" } } },
        { "@type": "ListItem", "position": 4, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 台中建築總部", "address": { "@type": "PostalAddress", "streetAddress": "台中市西屯區中工二路190號8樓", "addressLocality": "台中市", "addressCountry": "TW" } } },
        { "@type": "ListItem", "position": 5, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 台南", "address": { "@type": "PostalAddress", "streetAddress": "臺南市永康區中華里中華路12號13樓之3", "addressLocality": "臺南市", "addressCountry": "TW" } } },
        { "@type": "ListItem", "position": 6, "item": { "@type": "LocalBusiness", "name": "速築美宅家 Chronos Space - 屏東", "address": { "@type": "PostalAddress", "streetAddress": "屏東縣九如鄉仁愛街45巷19號1樓", "addressLocality": "屏東縣", "addressCountry": "TW" } } }
      ]
    }
  };

  return (
    <>
      <SEOMeta
        title="全省服務據點"
        description="速築美宅家 Chronos Space 提供台北、新竹、台中、台南、屏東與全省自地自建、危老重建、建築設計與預製工法諮詢服務，歡迎加入 LINE@ 預約。"
        keywords="全省服務據點,自地自建,台北,新竹,台中,台南,屏東,鋼構住宅,預製工法諮詢"
        canonicalPath="/locations"
        jsonLd={jsonLd}
      />

      <main>
      {/* ═══════════════════════════════════════
          SECTION 1 — HERO
         ═══════════════════════════════════════ */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt="台灣城市天際線"
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-graphite/70 via-graphite/50 to-graphite/75" />
        </div>

        <div className="relative z-10 w-full container-main px-4 md:px-8 lg:px-12 py-32 md:py-40">
          <div className="max-w-3xl">
            <span className="tag-label mb-6 inline-block">
              {t("locations.hero.label")}
            </span>
            <h1 className="text-white mb-6">
              {t("locations.hero.title")}
            </h1>
            <p className="text-cream/90 mb-8 max-w-2xl">
              {t("locations.hero.subtitle")}
            </p>
            <a
              href="https://lin.ee/OpssTIM"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary gap-2 text-[1.0625rem]"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill" />
              </span>
              {t("locations.hero.cta")}
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 2 — LOCATION CARDS
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {locationsData.map((loc, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 md:p-8 border border-warm-gray/50 transition-all duration-300 hover:-translate-y-1 hover:border-champagne/30"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-champagne/10 text-champagne shrink-0">
                      <i className={`${loc.icon} text-xl`} />
                    </span>
                    <div>
                      <h3 className="text-graphite">{t(loc.cityKey)}</h3>
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-warm-gray text-medium-gray text-base font-medium mt-1">
                        {t(loc.typeKey)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-6">
                  <span className="w-5 h-5 flex items-center justify-center text-medium-gray shrink-0 mt-0.5">
                    <i className="ri-map-pin-line" />
                  </span>
                  <p className="text-medium-gray">{t(loc.addressKey)}</p>
                </div>

                <a
                  href="https://lin.ee/OpssTIM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark w-full justify-center gap-2 text-base"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-line-fill" />
                  </span>
                  {t("locations.card.cta")}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DistanceCalculatorSection />

      {/* ═══════════════════════════════════════
          SECTION 3 — MAP + LOCATION LIST
         ═══════════════════════════════════════ */}
      <section className="bg-warm-gray section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10 md:mb-12">
            <span className="tag-label mb-4 inline-block">{t("locations.map.label")}</span>
            <h2 className="text-graphite">{t("locations.map.title")}</h2>
            <p className="text-medium-gray mt-3 max-w-2xl mx-auto">
              可先從下拉選單選擇服務據點，系統會在下方地圖中顯示對應位置。若地圖無法正常顯示，也可點擊「另開 Google Maps」查看。
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            {/* Map Card */}
            <div className="w-full lg:w-2/3">
              <div className="bg-white rounded-[22px] p-5 md:p-6 border border-warm-gray/50">
                {/* Form controls */}
                <form
                  method="GET"
                  action="https://www.google.com/maps"
                  target="mapframe"
                  className="flex flex-col sm:flex-row gap-3 mb-4"
                >
                  <div className="relative flex-1">
                    <select
                      name="q"
                      aria-label="選擇據點地址"
                      className="w-full rounded-xl border border-warm-gray/60 bg-cream px-4 py-3 pr-10 text-graphite text-base focus:outline-none focus:ring-2 focus:ring-champagne/50 appearance-none cursor-pointer"
                    >
                      <option value="台北市萬華區漢中街195號">台北｜台北市萬華區漢中街195號</option>
                      <option value="新竹縣竹北市自強南路372號3樓">新竹｜新竹縣竹北市自強南路372號3樓</option>
                      <option value="台中市北區中清路一段79號6樓">台中設計總部｜台中市北區中清路一段79號6樓</option>
                      <option value="台中市西屯區中工二路190號8樓">台中建築總部｜台中市西屯區中工二路190號8樓</option>
                      <option value="台中市南區大慶街二段55-5號">台中預製工法－大慶廠｜台中市南區大慶街二段55-5號</option>
                      <option value="臺南市永康區中華里中華路12號13樓之3">台南｜臺南市永康區中華里中華路12號13樓之3</option>
                      <option value="屏東縣九如鄉九清村14鄰仁愛街45巷19號1樓">屏東｜屏東縣九如鄉九清村14鄰仁愛街45巷19號1樓</option>
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-medium-gray">
                      <i className="ri-arrow-down-s-line text-xl" />
                    </span>
                  </div>
                  <input type="hidden" name="output" value="embed" />
                  <button
                    type="submit"
                    className="btn-primary whitespace-nowrap px-6 py-3 text-base"
                  >
                    查看地圖定位
                  </button>
                </form>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                  <a
                    href="https://www.google.com/maps?q=%E5%8F%B0%E7%81%A3&z=7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-champagne hover:text-graphite transition-colors text-base"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line" />
                    </span>
                    另開 Google Maps
                  </a>
                </div>

                {/* Map iframe with skeleton loader */}
                <div className="relative rounded-xl overflow-hidden border border-warm-gray/30">
                  {/* Skeleton overlay */}
                  {!mapLoaded && (
                    <div className="absolute inset-0 z-10 bg-warm-gray animate-pulse flex flex-col items-center justify-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-cream flex items-center justify-center">
                        <span className="w-8 h-8 flex items-center justify-center text-medium-gray">
                          <i className="ri-map-pin-2-line text-2xl" />
                        </span>
                      </div>
                      <div className="flex flex-col items-center gap-2">
                        <div className="h-5 w-40 bg-cream rounded" />
                        <div className="h-4 w-28 bg-cream/70 rounded" />
                      </div>
                    </div>
                  )}
                  <iframe
                    id="mapframe"
                    name="mapframe"
                    title="全省服務據點地圖"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps?q=%E5%8F%B0%E7%81%A3&z=7&output=embed"
                    className="w-full h-[380px] md:h-[520px]"
                    style={{ border: 0 }}
                    onLoad={() => setMapLoaded(true)}
                  />
                </div>
              </div>
            </div>

            {/* Location list sidebar */}
            <div className="w-full lg:w-1/3">
              <div className="bg-white rounded-[22px] p-6 md:p-8 border border-warm-gray/50 h-full">
                <h3 className="text-graphite mb-6">據點清單</h3>
                <div className="flex flex-col gap-4">
                  {locationsData.map((loc, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 pb-4 border-b border-warm-gray/30 last:border-0 last:pb-0"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-champagne/10 text-champagne shrink-0 text-base font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-graphite font-semibold text-base">
                          {t(loc.cityKey)}
                          <span className="text-medium-gray font-normal text-base ml-2">
                            {t(loc.typeKey)}
                          </span>
                        </p>
                        <p className="text-medium-gray text-base mt-0.5">{t(loc.addressKey)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-medium-gray text-base mt-6 pt-4 border-t border-warm-gray/30 leading-relaxed">
                  初次諮詢可先透過 LINE@ 提供基地位置、土地狀況與需求，我們會再協助安排適合的據點或線上說明。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 4 — CONSULT SCENARIOS
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10 md:mb-12">
            <span className="tag-label mb-4 inline-block">{t("locations.scenarios.label")}</span>
            <h2 className="text-graphite">{t("locations.scenarios.title")}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {consultScenarios.map((scenario, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 md:p-8 border border-warm-gray/50 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-champagne/10 text-champagne mx-auto mb-5">
                  <i className={`${scenario.icon} text-2xl`} />
                </span>
                <p className="text-graphite font-semibold text-[1.0625rem]">
                  {t(scenario.textKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 5 — PREPARATION CHECKLIST
         ═══════════════════════════════════════ */}
      <section className="bg-graphite section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <span className="tag-label mb-4 inline-block">{t("locations.prep.label")}</span>
              <h2 className="text-white">{t("locations.prep.title")}</h2>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-white/10">
              <div className="flex flex-col gap-4">
                {prepItems.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 py-3 border-b border-white/10 last:border-0"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-champagne/20 text-champagne shrink-0 text-base font-bold">
                      {i + 1}
                    </span>
                    <p className="text-cream/90 pt-1">{t(item)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 6 — FINAL CTA
         ═══════════════════════════════════════ */}
      <section className="bg-cream section-padding">
        <div className="container-main px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="tag-label mb-6 inline-block">{t("locations.final.label")}</span>
            <h2 className="text-graphite mb-6">{t("locations.final.title")}</h2>
            <p className="text-medium-gray mb-8">{t("locations.final.desc")}</p>
            <a
              href="https://lin.ee/OpssTIM"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary gap-2 text-[1.0625rem]"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill" />
              </span>
              {t("locations.final.cta")}
            </a>
          </div>
        </div>
      </section>
      </main>
    </>
  );
}